// ============================================================================
// PROJECT AVATARS TAB
// Displays and manages project-scoped avatar definitions for a project.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Bot, Plus, Pencil, Loader2, AlertTriangle, RefreshCw, EyeOff, Eye,
  TrendingUp, Layers, ToggleLeft, ToggleRight, Search, ChevronUp,
  ChevronDown, ChevronLeft, ChevronRight, ArrowUpDown, X
} from 'lucide-react';

import { AvatarDefinition, AvatarCategory } from '../../types';
import { getProjectAvatars, setProjectAvatarEnabled, getProjectAvatarPerformance, AvatarPerformanceStat } from '../../services/avatarCatalogService';
import { QuickCreateAvatarModal } from '../QuickCreateAvatarModal';
import { EditProjectAvatarModal } from './EditProjectAvatarModal';

interface ProjectAvatarsTabProps {
  projectId: string;
  token?: string;
}

const PAGE_SIZE = 10;

type SortKey = 'name' | 'category' | 'performance' | 'status';
type SortDir = 'asc' | 'desc';
type AvatarStatusFilter = 'all' | 'active' | 'disabled';

const CATEGORY_BADGE: Record<string, string> = {
  [AvatarCategory.CUSTOM]: 'bg-purple-50 text-purple-700 border border-purple-200',
  [AvatarCategory.FACILITATOR]: 'bg-blue-50 text-blue-700 border border-blue-200',
  [AvatarCategory.DOMAIN_EXPERT]: 'bg-amber-50 text-amber-700 border border-amber-200',
  [AvatarCategory.STAKEHOLDER]: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  [AvatarCategory.RESEARCHER]: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
};

const CATEGORY_OPTIONS = Object.values(AvatarCategory);

function avatarMatchesSearch(a: AvatarDefinition, query: string): boolean {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  return a.name.toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q);
}

function avatarMatchesFilters(
  a: AvatarDefinition,
  filterCategory: string,
  filterStatus: AvatarStatusFilter
): boolean {
  if (filterCategory && a.category !== filterCategory) return false;
  if (filterStatus === 'active' && a.isEnabled === false) return false;
  if (filterStatus === 'disabled' && a.isEnabled !== false) return false;
  return true;
}

function compareAvatars(
  a: AvatarDefinition,
  b: AvatarDefinition,
  sortKey: SortKey,
  performance: Record<string, AvatarPerformanceStat>
): number {
  switch (sortKey) {
    case 'name': return a.name.localeCompare(b.name);
    case 'category': return a.category.localeCompare(b.category);
    case 'performance': {
      const ra = performance[a.avatarDefinitionId]?.acceptanceRate ?? -1;
      const rb = performance[b.avatarDefinitionId]?.acceptanceRate ?? -1;
      return ra - rb;
    }
    case 'status': {
      const sa = a.isEnabled === false ? 1 : 0;
      const sb = b.isEnabled === false ? 1 : 0;
      return sa - sb;
    }
    default: return 0;
  }
}

function SortTh({
  label, sortK, currentKey, currentDir, onSort, className = ''
}: {
  label: string; sortK: SortKey; currentKey: SortKey; currentDir: SortDir;
  onSort: (k: SortKey) => void; className?: string;
}) {
  const active = currentKey === sortK;
  let sortIcon: React.ReactElement;
  if (active) {
    sortIcon = currentDir === 'asc'
      ? <ChevronUp size={12} className="text-primary" />
      : <ChevronDown size={12} className="text-primary" />;
  } else {
    sortIcon = <ArrowUpDown size={12} className="text-slate-200 group-hover:text-slate-400 transition-colors" />;
  }
  return (
    <th
      className={`px-5 py-3 text-left cursor-pointer select-none group ${className}`}
      onClick={() => onSort(sortK)}
    >
      <div className="flex items-center gap-1.5">
        <span className={`text-xs font-bold uppercase tracking-wider transition-colors ${active ? 'text-primary' : 'text-slate-400 group-hover:text-slate-600'}`}>
          {label}
        </span>
        {sortIcon}
      </div>
    </th>
  );
}

function getRateColorClass(rate: number | null): string {
  if (rate === null) return '';
  if (rate >= 60) return 'text-emerald-700 bg-emerald-50 border-emerald-200';
  if (rate >= 40) return 'text-amber-700 bg-amber-50 border-amber-200';
  return 'text-red-700 bg-red-50 border-red-200';
}

function ToggleIcon({ loading, disabled }: { loading: boolean; disabled: boolean }) {
  if (loading) {
    return <Loader2 size={18} className="animate-spin text-slate-400" />;
  }
  return disabled ? <ToggleLeft size={20} /> : <ToggleRight size={20} />;
}

function AvatarTableLoading() {
  return (
    <div className="flex justify-center items-center h-40">
      <Loader2 size={22} className="animate-spin text-slate-400" />
    </div>
  );
}

function AvatarTableEmpty({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <Bot size={28} className="text-slate-400" />
      </div>
      <h4 className="text-sm font-semibold text-navy mb-1">No project avatars yet</h4>
      <p className="text-sm text-slate-500 mb-5 max-w-xs">Create avatar definitions shared across all sessions in this project.</p>
      <button
        onClick={onCreate}
        className="flex items-center justify-center gap-2 rounded-xl h-9 px-4 bg-primary hover:bg-primary-dark transition-colors text-white text-xs font-bold shadow-sm"
      >
        <Plus size={14} /> Create First Avatar
      </button>
    </div>
  );
}

interface AvatarTableRowProps {
  avatar: AvatarDefinition;
  perf?: AvatarPerformanceStat;
  perfLoading: boolean;
  isToggling: boolean;
  onEdit: (avatar: AvatarDefinition) => void;
  onToggle: (avatar: AvatarDefinition) => void;
}

function AvatarTableRow({ avatar, perf, perfLoading, isToggling, onEdit, onToggle }: AvatarTableRowProps) {
  const isDisabled = avatar.isEnabled === false;
  const rate = perf ? Math.round(perf.acceptanceRate * 100) : null;
  const rateColor = getRateColorClass(rate);
  const builtUpon = perf ? (perf.builtUpon ?? Math.round(perf.total * perf.builtUponRate)) : 0;

  let performanceCell: React.ReactElement;
  if (perfLoading && !perf) {
    performanceCell = <Loader2 size={12} className="animate-spin text-slate-300" />;
  } else if (!perf) {
    performanceCell = <span className="text-xs text-slate-400">—</span>;
  } else {
    performanceCell = (
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-bold ${rateColor}`}>
          <TrendingUp size={10} />{rate}%
        </span>
        <span className="text-xs text-slate-500">{perf.accepted}/{perf.total}</span>
        {builtUpon > 0 && (
          <span className="flex items-center gap-0.5 text-xs text-indigo-500">
            <Layers size={10} />{builtUpon}
          </span>
        )}
        <span className="text-xs text-slate-400">{perf.sessions}s</span>
      </div>
    );
  }

  return (
    <tr className={`hover:bg-slate-50/60 transition-colors ${isDisabled ? 'opacity-60' : ''}`}>
      <td className="px-5 py-3.5">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden shrink-0 border-2"
            style={{
              background: isDisabled ? '#f1f5f9' : (avatar.secondaryColor || '#f1f5f9'),
              borderColor: isDisabled ? '#cbd5e1' : (avatar.primaryColor || '#e2e8f0'),
            }}
          >
            {avatar.imageUrl ? (
              <img src={avatar.imageUrl} alt={avatar.name} className="w-9 h-9 object-cover rounded-full" />
            ) : (
              <Bot size={16} color={isDisabled ? '#94a3b8' : (avatar.primaryColor || '#64748b')} />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-navy truncate">{avatar.name}</p>
            {avatar.description && (
              <p className="text-xs text-slate-500 truncate max-w-[220px]">{avatar.description}</p>
            )}
          </div>
        </div>
      </td>

      <td className="px-5 py-3.5">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${CATEGORY_BADGE[avatar.category] || CATEGORY_BADGE[AvatarCategory.CUSTOM]}`}>
          {avatar.category}
        </span>
      </td>

      <td className="px-5 py-3.5">
        {performanceCell}
      </td>

      <td className="px-5 py-3.5">
        {isDisabled ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-500 border border-slate-200">
            <EyeOff size={10} /> Disabled
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <Eye size={10} /> Active
          </span>
        )}
      </td>

      <td className="px-5 py-3.5">
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={() => onEdit(avatar)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-navy hover:bg-slate-100 transition-colors"
            title="Edit"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={() => onToggle(avatar)}
            disabled={isToggling}
            className={`transition-colors ${isDisabled ? 'text-slate-400 hover:text-slate-600' : 'text-primary hover:text-primary/80'}`}
            title={isDisabled ? 'Enable' : 'Disable'}
          >
            <ToggleIcon loading={isToggling} disabled={isDisabled} />
          </button>
        </div>
      </td>
    </tr>
  );
}

export const ProjectAvatarsTab: React.FC<ProjectAvatarsTabProps> = ({ projectId }) => {
  const [avatars, setAvatars] = useState<AvatarDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAvatar, setEditingAvatar] = useState<AvatarDefinition | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [performance, setPerformance] = useState<Record<string, AvatarPerformanceStat>>({});
  const [perfLoading, setPerfLoading] = useState(false);

  // Filter + sort + pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState<AvatarStatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [currentPage, setCurrentPage] = useState(1);

  const handleToggleEnabled = useCallback(async (avatar: AvatarDefinition) => {
    const newEnabled = avatar.isEnabled === false ? true : false;
    setTogglingId(avatar.avatarDefinitionId);
    try {
      const result = await setProjectAvatarEnabled(projectId, avatar.avatarDefinitionId, newEnabled);
      setAvatars(prev => prev.map(a => a.avatarDefinitionId === avatar.avatarDefinitionId ? result.avatar : a));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update avatar');
    } finally {
      setTogglingId(null);
    }
  }, [projectId]);

  const loadAvatars = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getProjectAvatars(projectId);
      setAvatars(result.avatars || []);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load project avatars');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const loadPerformance = useCallback(async () => {
    setPerfLoading(true);
    try {
      const data = await getProjectAvatarPerformance(projectId);
      setPerformance(data);
    } catch {
      // non-critical
    } finally {
      setPerfLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadAvatars();
    loadPerformance();
  }, [loadAvatars, loadPerformance]);

  const handleAvatarCreated = (avatar: AvatarDefinition) => {
    setShowCreateModal(false);
    setAvatars(prev => [avatar, ...prev]);
  };

  const handleAvatarSaved = (updated: AvatarDefinition) => {
    setEditingAvatar(null);
    setAvatars(prev => prev.map(a => a.avatarDefinitionId === updated.avatarDefinitionId ? updated : a));
  };

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const handleFilterChange = (fn: () => void) => { fn(); setCurrentPage(1); };

  const processedAvatars = useMemo(() => {
    const filtered = avatars.filter(a =>
      avatarMatchesSearch(a, searchQuery) && avatarMatchesFilters(a, filterCategory, filterStatus)
    );
    return [...filtered].sort((a, b) => {
      const cmp = compareAvatars(a, b, sortKey, performance);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [avatars, searchQuery, filterCategory, filterStatus, sortKey, sortDir, performance]);

  const totalPages = Math.max(1, Math.ceil(processedAvatars.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedAvatars = processedAvatars.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const hasFilters = searchQuery.trim() || filterCategory || filterStatus !== 'all';

  return (
    <div data-training-id="project-avatars-list" className="flex flex-col gap-6">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-navy">Project Avatars</h3>
          <p className="text-xs text-slate-500 mt-0.5">Avatar definitions shared across all sessions in this project.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadAvatars}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center justify-center gap-2 rounded-xl h-9 px-4 bg-primary hover:bg-primary-dark transition-colors text-white text-xs font-bold shadow-sm"
          >
            <Plus size={14} /> New Avatar
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm">
          <AlertTriangle size={16} className="shrink-0" />
          {error}
        </div>
      )}

      {/* Filter + Table */}
      <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
        {/* Filter bar */}
        <div className="p-4 border-b border-[#e2e8f0]">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[200px] relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name or description…"
                value={searchQuery}
                onChange={e => handleFilterChange(() => setSearchQuery(e.target.value))}
                className="w-full pl-9 pr-3 py-2 border border-[#e2e8f0] rounded-xl text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>
            <select
              value={filterCategory}
              onChange={e => handleFilterChange(() => setFilterCategory(e.target.value))}
              className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
            >
              <option value="">All categories</option>
              {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={filterStatus}
              onChange={e => handleFilterChange(() => setFilterStatus(e.target.value as AvatarStatusFilter))}
              className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
            {hasFilters && (
              <button
                onClick={() => { setSearchQuery(''); setFilterCategory(''); setFilterStatus('all'); setCurrentPage(1); }}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
              >
                <X size={13} /> Clear
              </button>
            )}
          </div>
        </div>

        {loading && avatars.length === 0 ? (
          <AvatarTableLoading />
        ) : !loading && avatars.length === 0 && !error ? (
          <AvatarTableEmpty onCreate={() => setShowCreateModal(true)} />
        ) : (
          <>
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                  <SortTh label="Avatar" sortK="name" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortTh label="Category" sortK="category" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortTh label="Performance" sortK="performance" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <SortTh label="Status" sortK="status" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                  <th className="px-5 py-3 w-28" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {pagedAvatars.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-sm text-slate-400">
                      No avatars match your filters
                    </td>
                  </tr>
                ) : pagedAvatars.map(avatar => (
                  <AvatarTableRow
                    key={avatar.avatarDefinitionId}
                    avatar={avatar}
                    perf={performance[avatar.avatarDefinitionId]}
                    perfLoading={perfLoading}
                    isToggling={togglingId === avatar.avatarDefinitionId}
                    onEdit={setEditingAvatar}
                    onToggle={handleToggleEnabled}
                  />
                ))}
              </tbody>
            </table>

            {/* Pagination footer */}
            {totalPages > 1 && (
              <div className="border-t border-[#e2e8f0] px-5 py-3 flex items-center justify-between text-sm text-slate-500">
                <span>
                  {processedAvatars.length === 0 ? '0 avatars' : `${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, processedAvatars.length)} of ${processedAvatars.length}`}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${
                        page === safePage
                          ? 'bg-primary/10 text-primary border border-primary/30'
                          : 'hover:bg-slate-100 text-slate-600'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showCreateModal && (
        <QuickCreateAvatarModal
          mode="manual"
          projectId={projectId}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleAvatarCreated}
        />
      )}

      {editingAvatar && (
        <EditProjectAvatarModal
          projectId={projectId}
          avatar={editingAvatar}
          onClose={() => setEditingAvatar(null)}
          onSaved={handleAvatarSaved}
        />
      )}
    </div>
  );
};

export default ProjectAvatarsTab;
